import type { ApiData } from './api-options';
import { parseResponseBody } from './api-response';

/**
 * Error thrown when an API request fails with status >= 400.
 * Includes the HTTP status code, original response, and parsed error data if available.
 */
export class ApiError extends Error {
  /**
   * Creates a new ApiError.
   *
   * @param message - Error message describing the failure
   * @param status - HTTP status code (e.g., 404, 500)
   * @param response - Original fetch Response object
   * @param data - Parsed error response body if available (typically JSON)
   */
  constructor(
    message: string,
    public status: number,
    public response: Response,
    public data?: ApiData,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Parses the response body and throws an ApiError with the parsed data.
 * Should only be called for responses with status >= 400.
 */
export async function handleErrorResponse(response: Response): Promise<never> {
  let errorData: ApiData;
  try {
    errorData = await parseResponseBody(response);
  } catch {
    errorData = 'Unable to parse response body.';
  }

  throw new ApiError(
    `API request failed with status ${response.status}`,
    response.status,
    response,
    errorData,
  );
}
